import React, { useState } from 'react';
import styled from 'styled-components';
import KubitModal from '../../KubitModal';
import { PrimaryButton, SecondaryButton } from '../../Button';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  CREATE_NAMED_FILTER,
  UPDATE_NAMED_FILTER,
} from '../../../utils/apollo/virtualEvents';
import { GET_NAMED_FILTERS } from '../../../pages/Formulas/apollo';
import {
  IConditionInput,
  ICreateNamedFilterMutation,
  ICreateNamedFilterMutationVariables,
  IFilterFieldsFragment,
  IGetNamedFiltersQuery,
  IGetNamedFiltersQueryVariables,
  IManageVersionEntityMutation,
  IManageVersionEntityMutationVariables,
  INamedFilterFieldsFragment,
  InputMaybe,
  IUpdateNamedFilterMutation,
  IUpdateNamedFilterMutationVariables,
  LogicalOperatorKey,
  ManagedEntityType,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import NamedFilterDropdown from './NamedFilterDropdown';
import { useStrategyOptions } from '../../../utils/hooks';
import RadioGroup from '../../RadioGroup';
import * as FormComponents from '../../Form';
import { CommentField, DescriptionField, NameField } from '../../Form';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { ModalActions } from '../../KubitModal/KubitModalUI';
import SaveEntityHeader, { SaveEntityHeaderEnum } from '../../SaveEntityHeader';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../../pages/PermissionControls/PermissionElementWrapper';
import { parseFilterToInput } from '../../../utils/filterFields';
import { SEARCH_TAGS } from '../../../utils/apollo/entitySelection/query';
import { ApplyTags } from '../../Categories';
import { useSaveCategories } from '../../../utils/categories';
import toastMessage from '../../../utils/toastMessage';
import {
  assertNotNil,
  ICreatableReactSelectOption,
} from '../../../utils/types';
import { MANAGE_VERSION_ENTITY } from '../../../utils/apollo/manageView/mutations';
import { defaultManagedOption } from '../../Categories/utils';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { filterSaved } from '../../../utils/analytics/events/filter/events';
import useQuickAccessDictionaryViewState from '../../../redux/selectorHooks/useQuickAccessDictionaryViewState';
import ThinDivider from '../../ThinDivider';
import { DESCRIPTION_MAX_LENGTH } from '../../../config/constants';

interface IProps {
  onClose: () => void;
  onSubmit: (namedFilter: INamedFilterFieldsFragment) => void;
  filter: IFilterFieldsFragment;
  schemaId: string;
}

const CommentFormGroup = styled(CommentField)`
  margin-top: 24px;
`;

const StrategyOptions = styled(RadioGroup)`
  margin-bottom: 30px;
`;
const TagsContainer = styled.div`
  margin-bottom: 24px;
`;

const SaveNamedFilterModal = ({
  onClose,
  onSubmit,
  filter, // IFilter with a Basic type, named filter types will never enter this component
  schemaId,
}: IProps) => {
  const client = useApolloClient();
  const { track } = useAnalytics();

  const { checkIsPermitted } = usePermission();
  const isPermittedToReadNamedFilter = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FilterRead],
    schemaId,
  });
  const isPermittedToCreateNamedFilter = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FilterCreate],
    schemaId,
  });

  const isPermittedToUpdateNamedFilter = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FilterEdit],
    schemaId,
  });
  const { strategy, setStrategy, strategyOptions } = useStrategyOptions({
    defaultStrategy: isPermittedToCreateNamedFilter ? 'create' : 'update',
    permittedStatus: {
      create: isPermittedToCreateNamedFilter,
      update: isPermittedToUpdateNamedFilter,
    },
  });
  const isDictionarySidebarExpanded = useQuickAccessDictionaryViewState();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [comment, setComment] = useState('');
  const [searchText, setSearchText] = useState('');
  const [namedFilters, setNamedFilters] = useState<
    INamedFilterFieldsFragment[]
  >([]);
  const [selectedId, setSelectedId] = useState<string>('');

  const onFetchNamedFilters = (res: IGetNamedFiltersQuery) => {
    const entities = res.namedFilters?.entities;
    if (entities) {
      setNamedFilters(entities);
    }
  };

  const skipFilterQuery =
    !isPermittedToReadNamedFilter ||
    strategy === 'create' ||
    !isPermittedToUpdateNamedFilter;
  const { loading } = useQuery<
    IGetNamedFiltersQuery,
    IGetNamedFiltersQueryVariables
  >(GET_NAMED_FILTERS, {
    variables: {
      schemaId,
      term: searchText,
      includeIds: [],
      pagination: {
        first: 50,
        after: 0,
      },
    },
    skip: skipFilterQuery,
    onCompleted: onFetchNamedFilters,
  });

  const [createNamedFilter, { loading: namedFilterIsCreating }] = useMutation<
    ICreateNamedFilterMutation,
    ICreateNamedFilterMutationVariables
  >(CREATE_NAMED_FILTER);

  const [updateNamedFilter, { loading: namedFilterIsUpdating }] = useMutation<
    IUpdateNamedFilterMutation,
    IUpdateNamedFilterMutationVariables
  >(UPDATE_NAMED_FILTER);

  const [manageVersionedEntity] = useMutation<
    IManageVersionEntityMutation,
    IManageVersionEntityMutationVariables
  >(MANAGE_VERSION_ENTITY);

  const {
    categories,
    setCategories,
    prepareCategories,
    isEntityManaged,
    setIsEntityManaged,
  } = useSaveCategories(schemaId);

  const onSelectNameChange = (
    selectedField: INamedFilterFieldsFragment | null,
  ) => {
    if (!selectedField) {
      setSelectedId('');
      setCategories([]);
      return;
    }
    const eventId = selectedField.id;
    setSelectedId(eventId);
    const namedFilter = namedFilters.find((nf) => nf.id === eventId);
    setDescription(namedFilter?.description ?? '');
    setCategories(
      namedFilter?.categories?.map((cat) => ({
        label: cat.name,
        value: cat.id,
      })) ?? [],
    );

    if (namedFilter?.isManaged) {
      setIsEntityManaged(true);
    }

    return;
  };

  const handleChangeTags = (values: ICreatableReactSelectOption[]) => {
    const managed = values.find(
      (value) => value.value === defaultManagedOption.value,
    );
    setIsEntityManaged(!!managed);
    setCategories(
      values.filter((value) => value.value !== defaultManagedOption.value),
    );
  };

  const handleChangeStrategy = (newStrategy: string) => {
    setStrategy(newStrategy);

    if (name) {
      setName('');
    }

    if (selectedId) {
      setSelectedId('');
    }

    if (description) {
      setDescription('');
    }

    if (categories.length > 0) {
      setCategories([]);
    }
  };

  const handleSubmit = async () => {
    let result;
    let successToastMessage: string;

    // first, parse the filter so we can convert to a BasicFilterInput.
    // parseFilterToInput will convert ICondition -> IConditionInput & ILogicOp -> ILogicOpInput
    // and return an IBasicFilterInput, which can be used to create/update a named filter.
    const parsedFilter = parseFilterToInput(filter, schemaId);
    if (!parsedFilter?.basicFilter) {
      return;
    }
    const { conditions, logicOps } = parsedFilter.basicFilter;

    const inputCategories = await prepareCategories();

    if (strategy === 'create') {
      const response = await createNamedFilter({
        variables: {
          input: {
            schema: schemaId,
            description,
            comment,
            name,
            conditions,
            logicOps,
            categories: inputCategories,
          },
        },
      });
      result = { strategy, response };
      successToastMessage = `${name} has been created.`;
    } else {
      // temporary solution for new type mappings from kobol
      const filterConditions: InputMaybe<IConditionInput[]> =
        conditions as InputMaybe<IConditionInput[]>;
      const filterLogicOps: InputMaybe<LogicalOperatorKey[]> =
        logicOps as InputMaybe<LogicalOperatorKey[]>;

      // name is id here, therefore search for id in entities
      const response = await updateNamedFilter({
        variables: {
          input: {
            name: null,
            description,
            comment,
            namedFilterId: selectedId,
            conditions: filterConditions,
            logicOps: filterLogicOps,
            categories: inputCategories,
          },
        },
      });
      result = { strategy, response };
      const namedFilter = namedFilters.find((ve) => ve.id === selectedId);
      successToastMessage = `${namedFilter?.name} has been updated.`;
    }

    // check errors and show toast
    const errors =
      result.strategy === 'create'
        ? result.response?.data?.namedFilterCreate?.errors
        : result.response?.data?.namedFilterEdit?.errors;

    if (printGraphqlErrors(errors)) {
      return;
    } else {
      toastMessage({ message: successToastMessage, type: 'info' });
    }

    const newNamedFilter =
      result.strategy === 'create'
        ? result.response?.data?.namedFilterCreate?.entity
        : result.response?.data?.namedFilterEdit?.entity;
    assertNotNil(newNamedFilter);
    const entityId = newNamedFilter.id;

    if (isEntityManaged && entityId) {
      const res = await manageVersionedEntity({
        variables: {
          input: {
            entityId,
            entityType: ManagedEntityType.Filter,
            isManaged: true,
          },
        },
      });

      if (res.data?.manageVersionEntity?.errors) {
        printGraphqlErrors(res.data.manageVersionEntity.errors);
      }
    }

    // refetch latest named filters for dictionary sidebar
    if (isDictionarySidebarExpanded) {
      client.refetchQueries({
        include: [SEARCH_TAGS],
      });
    }

    track(filterSaved(newNamedFilter));

    onSubmit(newNamedFilter);
  };

  const isSaveDisabled =
    loading ||
    (!name.length && !selectedId) ||
    name.length > 50 ||
    namedFilterIsCreating ||
    namedFilterIsUpdating ||
    !isPermittedToReadNamedFilter ||
    description.length > DESCRIPTION_MAX_LENGTH ||
    comment.length > DESCRIPTION_MAX_LENGTH;

  return (
    <KubitModal open onClose={onClose} header="Save Named Filter">
      <SaveEntityHeader entityType={SaveEntityHeaderEnum.NamedFilter} />
      <StrategyOptions
        name="strategy"
        value={strategy}
        onChange={handleChangeStrategy}
        options={strategyOptions}
        hasPermissionCheck={true}
      />
      {strategy === 'create' ? (
        <NameField
          label={'Name'}
          value={name}
          onChange={setName}
          placeholder="Enter a new Named Filter name"
          maxLength={50}
          targetErrorElement="Filter"
          disabled={loading || namedFilterIsCreating}
        />
      ) : (
        <FormComponents.VerticalField>
          <FormComponents.FieldLabel>Name</FormComponents.FieldLabel>
          <PermissionElementWrapper isPermitted={isPermittedToReadNamedFilter}>
            <NamedFilterDropdown
              filterFieldOptions={[]}
              namedFilterOptions={namedFilters}
              selected={
                namedFilters?.find((ve) => ve.id === selectedId) ?? null
              }
              onChange={onSelectNameChange}
              searchText={searchText}
              onSearchChange={setSearchText}
              disabled={!isPermittedToReadNamedFilter}
            />
          </PermissionElementWrapper>
        </FormComponents.VerticalField>
      )}
      <DescriptionField
        label={'Description'}
        value={description}
        onChange={setDescription}
        placeholder="Add filter description here"
        targetErrorElement="Filter"
        disabled={loading || namedFilterIsCreating || namedFilterIsUpdating}
      />
      <TagsContainer>
        <ApplyTags
          schemaId={schemaId}
          selectedValues={categories}
          onChange={handleChangeTags}
          isManaged={isEntityManaged}
        />
      </TagsContainer>
      <ThinDivider layout="horizontal" size="full" />
      <CommentFormGroup
        label={'Comment'}
        value={comment}
        onChange={setComment}
        placeholder="Add your comment here"
        targetErrorElement="Filter"
        disabled={loading || namedFilterIsCreating || namedFilterIsUpdating}
      />
      <ModalActions>
        <SecondaryButton
          type="button"
          onClick={onClose}
          disabled={namedFilterIsCreating || namedFilterIsUpdating}
          trackContext="named filter"
          trackAction="cancel"
        >
          Cancel
        </SecondaryButton>
        {/* only need to check read permission, if update is not permitted, won't be able to access the page */}
        <PermissionElementWrapper isPermitted={isPermittedToReadNamedFilter}>
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={isSaveDisabled}
            trackContext="named filter"
            trackAction="save"
          >
            Save
          </PrimaryButton>
        </PermissionElementWrapper>
      </ModalActions>
    </KubitModal>
  );
};

export default SaveNamedFilterModal;
